<script>
import utils from '@/utils/common';
import modelCustomSettings from '@/model/userCustomSettings';
import stageApi from '@/api/stage';

import FileList from '@/components/FileList.vue';
import BtnExpand from '@/components/ui/BtnExpand.vue';
import ImageSliderWrap from '@/components/ui/ImageSliderWrap.vue';
import BaseTabs3 from '@/components/BaseTabs3.vue';

export default {
  name: 'ProjectFileAttachStageTile',
  components: { BaseTabs3, ImageSliderWrap, FileList, BtnExpand },
  props: {
    project: Object,
    stage: Object,
    external: Boolean,
    downloadHrefBase: String,
  },
  data() {
    return {
      sliderMode: false,
      selectedTaskIri: null,
    };
  },
  computed: {
    currUser() { return this.$store.state.auth?.user ?? {}; },
    foldedStages() { return this.currUser?.customSettings?.foldedStagesInAttaches ?? []; },
    projectId() { return this.external ? null : this.project.id; },
    taskOptions() {
      return this.stage.tasks.map(t => ({ value: t['@id'], label: t.name }));
    },
    selectedTask() {
      return this.stage.tasks.find(task => task['@id'] === this.selectedTaskIri);
    },
    separatedFiles() {
      const images = [];
      const docs = [];

      this.selectedTask.attaches.forEach((item) => {
        (item.isImage ? images : docs).push(item);
      });

      return { images, docs };
    },
    stats() {
      const reduceTask = task => task.attaches
        .reduce((acc, val) => {
          acc[0] += val.file.size;
          acc[1] += 1;
          return acc;
        }, [0, 0]);


      const res = this.stage.tasks
        .reduce((acc, val) => {
          const reduced = reduceTask(val);
          acc[0] += reduced[0];
          acc[1] += reduced[1];
          return acc;
        }, [0, 0]);

      return [
        utils.storageSizeCounter(res[0]),
        utils.pluralWord(res[1], 'файл', 'файла', 'файлов'),
      ];
    },
  },
  watch: {
    stage: {
      immediate: true,
      handler(val) {
        this.sliderMode = val?.settings?.fileAttachSliderMode ?? false;
      },
    },
    sliderMode: {
      immediate: true,
      handler(val) {
        if (!val) return;
        if (this.selectedTaskIri) return;

        this.selectedTaskIri = this.stage.tasks[0]['@id'];
      },
    },
  },
  methods: {
    async changeSliderMode(enabled) {
      this.sliderMode = enabled;

      if (this.external) return;

      const settings = this.stage.settings ?? {};
      settings.fileAttachSliderMode = enabled;

      await stageApi.update(this.stage.id, { settings });
    },
    toggleStageFolding(stage) {
      const foldedStagesInAttaches = this.foldedStages.includes(stage.id)
        ? this.foldedStages.filter(id => id !== stage.id)
        : [...this.foldedStages, stage.id];

      modelCustomSettings.set({ foldedStagesInAttaches });
    },
  },
}
</script>

<template lang="pug">
  .b-project-file-attach-stage-tile
    ._stage-header
      ._title {{ stage.name }}
      button._slider-btn(:class="{ active: sliderMode }" @click="changeSliderMode(true)")
        Icon(name="slider-enabled" colored pointer)
      button._slider-btn(:class="{ active: !sliderMode }" @click="changeSliderMode(false)")
        Icon(name="slider-disabled" colored pointer)
      BtnExpand(v-if="!external" :expanded="!foldedStages.includes(stage.id)" @toggle="toggleStageFolding(stage)")
    ._stage-content(v-if="!foldedStages.includes(stage.id)")
      ._slider-wrap(v-if="sliderMode")
        BaseTabs3._slider-tabs(:tabs="taskOptions" v-model="selectedTaskIri" v-if="taskOptions.length > 1")
        ImageSliderWrap._slider(:items="separatedFiles.images")
        FileList(
          :files="separatedFiles['docs']"
          :attached="external ? [] : separatedFiles['docs']"
          :attachable="!external"
          @detach="$emit('detach', $event)"
        )
      ._tasks(v-else)
        ._task(v-for="task in stage.tasks" :key="task['@id']")
          template(v-if="projectId")
            Link._task-name(:to="{ name: 'task-detail', params: { projectId: projectId, stageId: stage.id, taskId: task.id } }") {{ task.name }}
          ._task-name(v-else) {{ task.name }}
          FileList(
            :files="task.attaches"
            :attached="external ? [] : task.attaches"
            :attachable="!external"
            @detach="$emit('detach', $event)"
          )
      ._footer
        ._stats {{ stats[1] }} {{ stats[0] }}
        Btn(dark :href="`${downloadHrefBase}?stages[]=${stage.id}`")
          Icon(name="download" colored)
          | Скачать файлы раздела
</template>

<style scoped lang="scss">
.project-file-attach-stage-tile {
  display: flex;
  flex-direction: column;
  gap: 25px;


  &__title {
    font-size: 18px;
  }

  &__stage-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    & > :first-child {
      margin-right: auto;
    }
  }

  &__slider-btn {
    background: transparent;

    svg {
      display: block;
      color: #788CA1;
    }

    &.active svg {
      color: #33A1FB;
    }
  }

  &__stage-content {
    display: flex;
    flex-direction: column;
    gap: 38px;
  }

  &__slider-wrap {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  &__slider-tabs {
    align-self: center;
  }

  &__tasks {
    display: flex;
    flex-direction: column;
    gap: 38px;
  }

  &__task {
    padding: 20px;
    border-radius: 10px;
    background: #F5F7F9;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  &__footer {
    margin-top: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    svg {
      color: #5AD9D1;
    }
  }

  &__stats {
    font-size: 14px;
    font-weight: 500;
    color: #788CA1;
  }

  @media (max-width: 575px) {
    &__footer {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 20px;
    }
  }
}
</style>
